import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, useIntl } from 'react-intl';
import isEqual from 'lodash-es/isEqual';
import moment from 'moment';
import momentPropTypes from 'react-moment-proptypes';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaCalendar } from 'react-icons/fa';

import intlShape from '@/shared/utils/intlShape';
import RegistrationComponent from '@/shared/components/registration/RegistrationComponent';
import OkCancelModal from '@/shared/components/OkCancelModal';
import api from '@/shared/api';
import config from '@/config';
import ToastrService from '@/shared/services/ToastrService';
import handleHttpError from '@/shared/services/HandleHttpError';
import AuthService, { functionalities } from '@/shared/services/AuthService';
import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import classNames from 'classnames';

import {
  createEmptyRegistration,
  executeAction,
  isRegistrationValid,
  registrationShape,
} from './registrationHelpers';
import messages from './registrationMessages';
import { FormGroupPart } from './partials';

const registrationOptions = {
  showDate: false,
  showReferenceYear: false,
  showTasks: false,
  showFromUntil: false,
  showDuration: false,
  showQuantity: false,
  showPrice: false,
  showDiscount: false,
  showVat: false,
  showTotalAmount: false,
  showBillable: false,
  showInForfait: false,
  showInternalRemark: false,
  showInvoiceRemark: false,
  showInteraction: false,
  ownCompanyRelation: true,
};

class FillGapsRegistrationModal extends Component {
  state = {
    registration: null,
    dateFrom: moment.utc().startOf('day'),
    dateUntil: moment.utc().startOf('day'),
    userId: AuthService.getUserId(),
    gapInfo: null,
    totalTime: 0,
    showValidation: false,
  };

  constructor(props) {
    super(props);

    this.state.dateFrom = props.dateFrom;
    this.state.dateUntil = props.dateUntil;
    this.state.userId = props.userId || AuthService.getUserId();
  }

  componentDidMount() {
    const { registration: initRegistration } = this.props;

    const registration = createEmptyRegistration(initRegistration);

    this.setState({ registration, isRecup: !!registration && registration.type === 4 }, () =>
      this.reloadGapInfo(),
    );
  }

  doRegistrationAction = (action, value) => {
    this.setState(({ registration }) => {
      const newValue = executeAction(registration, action, value);
      if (isEqual(newValue, registration)) return {};
      return { registration: newValue };
    });
  };
  saveRegistration = () => {
    if (this.isFormInvalid()) return;
    const { intl, onSave } = this.props;
    const { registration, userId, dateFrom, dateUntil, isRecup } = this.state;

    return api.base
      .post(`${config.appUrls.api}/api/Registration/FillDays`, {
        userId,
        dateFrom,
        dateUntil,
        isRecup,
        registration,
      })
      .then(
        ({ data }) => {
          onSave(data);
          ToastrService.success();
        },
        error => {
          handleHttpError(intl)(error);
        },
      );
  };
  isFormInvalid = () => {
    const { registration, dateFrom, dateUntil, totalTime } = this.state;
    if (!dateFrom || !dateUntil) return true;
    if (!moment.utc(dateFrom).isValid()) return true;
    if (!moment.utc(dateUntil).isValid()) return true;
    if (moment.utc(dateUntil).isBefore(moment.utc(dateFrom))) return true;

    if (!isRegistrationValid(registration, registrationOptions)) return true;

    return false;
  };

  showValidation = () => this.setState({ showValidation: true });
  hasSavePermission = () => {
    const { registration } = this.state;
    if (!registration) return false;
    if (registration.userId && registration.userId !== AuthService.getUserId())
      return AuthService.hasPermission({
        functionality: functionalities.RegistrationOthers,
        privilege: 'Update',
      });
    return AuthService.hasPermission({
      functionality: functionalities.RegistrationOwn,
      privilege: 'Update',
    });
  };

  reloadGapInfo = () => {
    const { userId, dateFrom, dateUntil, isRecup } = this.state;
    if (!userId || !dateFrom || !dateUntil) {
      this.setState({ gapInfo: null });
      return;
    }

    api.base
      .post(`${config.appUrls.api}/api/Registration/CalculateGaps`, {
        userId,
        dateFrom,
        dateUntil,
        isRecup,
      })
      .then(
        ({ data }) => {
          this.setState({
            gapInfo: data,
            totalTime: (data || []).reduce((sum, day) => sum + day.durationMinutes, 0),
          });
        },
        error => {
          const { intl } = this.props;
          handleHttpError(intl)(error);
        },
      );
  };
  datesChanged = ({ startDate, endDate }) => {
    this.setState({ dateFrom: startDate, dateUntil: endDate }, this.reloadGapInfo);
  };

  render() {
    const { show, intl, onClose, remainingOvertime } = this.props;
    const { registration, showValidation, dateFrom, dateUntil, gapInfo, totalTime } = this.state;
    const hasRegistrationPermission = this.hasSavePermission();
    const remainingOvertimeAfterSave = (remainingOvertime || 0) - (totalTime || 0);
    return (
      <OkCancelModal
        title={intl.formatMessage(messages.Registration)}
        show={show}
        onClose={onClose}
        disabled={!hasRegistrationPermission || (showValidation && this.isFormInvalid())}
        onSave={this.saveRegistration}
        onHoverSave={this.showValidation}
      >
        <div className="form-horizontal">
          <div className="form-group">
            <FormGroupPart label={messages.RegTimeFromTo} inputCols="col-sm-9" labelCols="col-sm-3">
              <DelayedDateRangePicker
                startDate={dateFrom}
                endDate={dateUntil}
                onDatesChange={this.datesChanged}
                block
              />
            </FormGroupPart>
          </div>
          {gapInfo ? (
            <div className="form-group">
              <FormGroupPart
                label={messages.TotalDuration}
                inputCols="col-sm-3 form-control-static"
                labelCols="col-sm-3"
              >
                <span className="margin10R">{totalTime.minuteToTime()}</span>
                <OverlayTrigger
                  trigger={['hover', 'focus']}
                  rootClose
                  placement="right"
                  overlay={
                    <Tooltip id="durationDaysFieldInfoTooltip">
                      <table>
                        <thead>
                          <th>
                            {intl.formatMessage(messages.Date)} ({gapInfo.length})
                          </th>
                          <th>{intl.formatMessage(messages.Duration)}</th>
                        </thead>
                        <tbody>
                          {gapInfo.map((x, i) => {
                            if (i === 26 && gapInfo.length > 31)
                              return (
                                <tr key="dot">
                                  <td colSpan="2">...</td>
                                </tr>
                              );
                            if (i > 26 && i < gapInfo.length - 5) return null;
                            return (
                              <tr key={x.date}>
                                <td className="text-right text-nowrap">
                                  {intl.formatDate(x.date, {
                                    weekday: 'short',
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                  })}
                                  &nbsp;&nbsp;&nbsp;
                                </td>
                                <td className="text-right text-nowrap">
                                  {x.durationMinutes.minuteToTime()}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Tooltip>
                  }
                >
                  <span className="clickable">
                    <FaCalendar className="react-icon" />
                  </span>
                </OverlayTrigger>
              </FormGroupPart>
              {registration && registration.type === 4 ? (
                <FormGroupPart
                  label={messages.RemainingOvertimeHours}
                  inputCols="col-sm-3 form-control-static text-right"
                  labelCols="col-sm-3"
                  labelAlign="right"
                >
                  {(remainingOvertime || 0).minuteToTime()}
                </FormGroupPart>
              ) : null}
            </div>
          ) : null}
          {gapInfo && registration && registration.type === 4 ? (
            <div className="form-group">
              <FormGroupPart
                label={messages.RemainingOvertimeHoursAfterSave}
                inputCols={classNames('col-sm-3 form-control-static text-right', {
                  'text-danger': remainingOvertimeAfterSave < 0,
                })}
                labelCols="col-sm-9"
                labelAlign="right"
              >
                {remainingOvertimeAfterSave.minuteToTime()}
              </FormGroupPart>
            </div>
          ) : null}
          {registration && (!!registration.type || registration.type === 0) ? (
            <RegistrationComponent
              registration={registration}
              onAction={this.doRegistrationAction}
              options={registrationOptions}
              showValidation={showValidation}
            />
          ) : null}
        </div>
      </OkCancelModal>
    );
  }
}

FillGapsRegistrationModal.propTypes = {
  registration: registrationShape,
  onSave: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  onClose: PropTypes.func.isRequired,
  show: PropTypes.bool,
  dateFrom: momentPropTypes.momentObj,
  dateUntil: momentPropTypes.momentObj,
  userId: PropTypes.string,
  remainingOvertime: PropTypes.number,
};

FillGapsRegistrationModal.defaultProps = {
  show: false,
  registration: null,
  dateFrom: moment.utc().startOf('day'),
  dateUntil: moment.utc().startOf('day'),
  userId: null,
  remainingOvertime: null,
};
export default injectIntl(FillGapsRegistrationModal);
